import { type RefObject } from 'react';

import { createHeightSync } from './createHeightSync';

const titleHeightSync = createHeightSync();

/**
 * Coordinates the card title height across the sibling offer cards. Each card reports the natural
 * height of its (unconstrained) title and the provider tracks the tallest one, so every card can
 * reserve exactly that height. This keeps the subtitle, price and expandable rows aligned across
 * the cards without hard-coding a fixed min-height that looks off when a title only takes one line.
 */
export const TitleHeightSyncProvider = titleHeightSync.Provider;

/**
 * Measures the natural height of a title element and syncs it with the sibling cards via the
 * {@link TitleHeightSyncProvider}. Returns two refs: `titleRef` for the (unconstrained) element to
 * measure, and `wrapperRef` for the element that reserves the shared max height. The reservation is
 * applied imperatively (no inline styles) and falls back to none when rendered outside a provider.
 *
 * @param enabled - Skip measuring/registering when there is no title to align.
 */
export const useTitleHeightSync = (
  enabled = true,
): { titleRef: RefObject<HTMLDivElement | null>; wrapperRef: RefObject<HTMLDivElement | null> } => {
  const { measureRef, reserveRef } = titleHeightSync.useHeightSync(enabled);
  return { titleRef: measureRef, wrapperRef: reserveRef };
};
